import React, { useState } from "react";
import { Box, Link, Skeleton, Typography } from "@mui/material";
import moment from "moment";
import tickIcon from "../../../../assets/tick.svg";
import {
  billingHead,
  dueOn,
  premiumHead,
  dateFormat,
} from "../../../../constants/Constants";
import CustomTable, {
  IColumn,
} from "../../../../common/CustomTable/CustomTable";
import { currencyFormat } from "../../../../utils/Utility";
import CustomDialog from "../../../../common/CustomDialog/CustomDialog";
import { IPolicyDetailsHeader } from "../../../../models/user/policyDetails/PolicyDetails";
import BasicInfoCardStyles from "./BasicInfoCardStyles";
import CustomIcon from "../../../../common/CustomIcon/CustomIcon";
import { t } from "i18next";
import CustomDrawer from "../../../../common/CustomDrawer/CustomDrawer";
import { useAppSelector } from "../../../../store/hooks";

interface IProps {
  data: IPolicyDetailsHeader | null;
}

const BasicInfoCard: React.FC<IProps> = ({ data }: IProps) => {
  const [billingDetailsDialogOpen, setBillingDetailsDialogOpen] =
    useState<boolean>(false);
  const { calledReducerType, isLoading } = useAppSelector(
    (state) => state.common
  );
  const getIcon = (lobCode: string) => {
    switch (lobCode) {
      case "PA":
        return <CustomIcon iconName="Car" />;
      case "HO":
        return <CustomIcon iconName="Home" />;
      case "PU":
        return <CustomIcon iconName="Umbrella" />;
      case "BOP":
        return <CustomIcon iconName="Business" width="60px" height="60px" />;
      case "FO":
        return <CustomIcon iconName="Farm" />;
    }
  };

  const getTitleInBasicInfo = (lobCode: string) => {
    switch (lobCode) {
      case "PA":
        return t("Insured");
      case "HO":
        return t("Risk Address");
      case "PU":
        return t("Personal Excess Liability");
      case "BOP":
        return t("Business Name");
      case "FO":
        return t("Insured");
    }
  };

  const getRiskText = () => {
    if (data) {
      switch (data.lobCode) {
        case "PA":
          return data.insuredName;
        case "HO":
          return (
            data.riskList[0].address.addr1 +
            " " +
            data.riskList[0].address.city +
            " " +
            data.riskList[0].address.stateProvCd +
            " " +
            data.riskList[0].address.regionISOCd +
            " " +
            data.riskList[0].address.postalCode
          );
        case "PU":
          return currencyFormat("us", data.personalUmbrellaLiabilityLimit);
        case "BOP":
          return data.insuredList?.map((insured) =>
            insured.dBAName ? insured.dBAName : insured.businessName
          );
        case "FO":
          return data.insuredName;
      }
    }
  };

  const columns: IColumn[] = [
    {
      label: "Invoice Date",
      accessorKey: "invoiceDate",
      Cell: (bill: any) => (
        <Typography
          sx={{
            fontFamily: "Noto Sans",
            fontSize: "14px",
            fontWeight: 400,
            whiteSpace: "initial",
          }}
        >
          {`${moment(bill.invoiceDate).format(dateFormat)}`}
        </Typography>
      ),
    },
    {
      label: "Due Date",
      accessorKey: "invoiceDueDate",
      Cell: (bill: any) => (
        <Typography
          sx={{
            fontFamily: "Noto Sans",
            fontSize: "14px",
            fontWeight: 400,
            whiteSpace: "initial",
          }}
        >
          {`${moment(bill.invoiceDueDate).format(dateFormat)}`}
        </Typography>
      ),
    },
    {
      label: "Status",
      styles: { textAlign: "center" },
      accessorKey: "invoiceStatus",
      Cell: (bill: any) => (
        <Typography
          sx={{
            textAlign: "center",
            fontFamily: "Noto Sans",
            fontSize: "14px",
            fontWeight: 400,
            whiteSpace: "initial",
          }}
        >
          {bill.invoiceStatus}
        </Typography>
      ),
    },
    {
      label: "Premium Amount",
      accessorKey: "premiumAmount",
      styles: { textAlign: "right" },
      Cell: (bill: any) => (
        <Typography
          sx={{
            textAlign: "right",
            fontFamily: "Noto Sans",
            fontSize: "14px",
            fontWeight: 400,
            whiteSpace: "initial",
          }}
        >
          {currencyFormat("us", bill.premiumAmount)}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <Box
        id="basicpolicydetails_container"
        data-test-id="test-basicpolicydetails_container"
        aria-label="Basic Policy Details Container"
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "8% 92%",
          padding: "20px",
          gap: "20px",
          "@media (max-width: 768px)": {
            gridTemplateColumns: "20% 80%",
          },
        }}
      >
        <Box
          id="basicpolicydetails_icon-section"
          data-test-id="test-basicpolicydetails_icon-section"
          aria-label="Policy Icon Section"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "10px",
          }}
        >
          {data && data.lobCode ? getIcon(data?.lobCode) : <></>}
          <Box
            id="basicpolicydetails_status"
            data-test-id="test-basicpolicydetails_status"
            aria-label="Policy Status"
            sx={BasicInfoCardStyles.status}
          >
            {data && data.carrierPolicyStatus === "Active" && (
              <img src={tickIcon} alt="tick-logo" />
            )}
            <Typography
              id="basicpolicydetails_status-text"
              data-test-id="test-basicpolicydetails_status-text"
              aria-label="Policy Status Text"
              sx={BasicInfoCardStyles.statusText}
            >
              {data && data.carrierPolicyStatus ? data.carrierPolicyStatus : ""}
            </Typography>
          </Box>
        </Box>
        <Box
          id="basicpolicydetails_details"
          data-test-id="test-basicpolicydetails_details"
          aria-label="Policy Details"
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            "@media (max-width: 768px)": {
              gridTemplateColumns: "1fr",
            },
          }}
        >
          <Box
            id="basicpolicydetails_insured-info"
            data-test-id="test-basicpolicydetails_insured-info"
            aria-label="Insured Information"
            sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <Typography
              id="basicpolicydetails_insured-title"
              data-test-id="test-basicpolicydetails_insured-title"
              aria-label="Insured Title"
              sx={BasicInfoCardStyles.head}
            >
              {data && data.lobCode ? getTitleInBasicInfo(data?.lobCode) : ""}
            </Typography>
            <Box
              id="basicpolicydetails_insured-details"
              data-test-id="test-basicpolicydetails_insured-details"
              aria-label="Insured Details"
              sx={{
                display: "flex",
                gap: "12px",
                "@media (max-width: 1331px)": {
                  flexDirection: "column",
                  gap: "5px",
                },
              }}
            >
              {isLoading &&
              calledReducerType === "policyDetails/GetPolicyDetailsHeader" ? (
                <Skeleton animation="wave" variant="text" width="100px" />
              ) : (
                <Typography
                  id="basicpolicydetails_insured-name"
                  data-test-id="test-basicpolicydetails_insured-name"
                  aria-label="Insured Name"
                  sx={BasicInfoCardStyles.content}
                >
                  {data ? getRiskText() : ""}{" "}
                  {data &&
                  data.lobCode === "PA" &&
                  data.accountType === "Joint" &&
                  data.insuredNameJoint
                    ? `| ${data.insuredNameJoint}`
                    : ""}
                </Typography>
              )}
              {/* <Typography sx={BasicInfoCardStyles.content}>
                | {data && data.riskCount ? data.riskCount : "0"}{" "}
                {data && data.lobCode ? getRiskType(data?.lobCode) : ""}
              </Typography> */}
            </Box>
          </Box>

          <Box
            id="basicpolicydetails_billing-info"
            data-test-id="test-basicpolicydetails_billing-info"
            aria-label="Billing Information"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              "@media (max-width: 768px)": {
                flexDirection: "column",
                gap: "5px",
              },
            }}
          >
            <Typography
              id="basicpolicydetails_billing-title"
              data-test-id="test-basicpolicydetails_billing-title"
              aria-label="Billing Title"
              sx={BasicInfoCardStyles.head}
            >
              {`${t(billingHead)}`}
            </Typography>
            {isLoading &&
            calledReducerType === "policyDetails/GetPolicyDetailsHeader" ? (
              <Skeleton animation="wave" variant="text" width="100px" />
            ) : (
              <Typography
                id="basicpolicydetails_billing-details"
                data-test-id="test-basicpolicydetails_billing-details"
                aria-label="Billing Details"
                sx={BasicInfoCardStyles.content}
              >
                {data?.dueAmount && data.dueDate
                  ? ` ${currencyFormat(
                      "us",
                      +parseFloat(data?.dueAmount).toFixed(2)
                    )} | ${dueOn} ${moment(data.dueDate).format(dateFormat)}`
                  : t("No Pending Dues")}
              </Typography>
            )}
          </Box>

          <Box
            id="basicpolicydetails_premium-info"
            data-test-id="test-basicpolicydetails_premium-info"
            aria-label="Premium Information"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography
              id="basicpolicydetails_premium-title"
              data-test-id="test-basicpolicydetails_premium-title"
              aria-label="Premium Title"
              sx={BasicInfoCardStyles.head}
            >
              {`${t(premiumHead)}`}
            </Typography>
            <Box
              id="basicpolicydetails_premium-details"
              data-test-id="test-basicpolicydetails_premium-details"
              aria-label="Premium Details"
              sx={{
                display: "flex",
                gap: "12px",
                "@media (max-width: 768px)": {
                  flexDirection: "column",
                  gap: "5px",
                },
              }}
            >
              {isLoading &&
              calledReducerType === "policyDetails/GetPolicyDetailsHeader" ? (
                <Skeleton animation="wave" variant="text" width="100px" />
              ) : (
                <>
                  <Typography
                    id="basicpolicydetails_premium-amount"
                    data-test-id="test-basicpolicydetails_premium-amount"
                    aria-label="Premium Amount"
                    sx={BasicInfoCardStyles.content}
                  >
                    {data
                      ? currencyFormat("us", Number(data.finalPremiumAmount))
                      : ""}{" "}
                    / {data ? data.tenure : ""}
                  </Typography>
                  <Typography
                    id="basicpolicydetails_premium-dates"
                    data-test-id="test-basicpolicydetails_premium-dates"
                    aria-label="Premium Dates"
                    sx={BasicInfoCardStyles.content}
                  >
                    |{" "}
                    {data?.effectiveDate
                      ? `${moment(data.effectiveDate).format(
                          dateFormat
                        )} - ${moment(data.expiryDate).format(dateFormat)}`
                      : ""}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Box
            id="basicpolicydetails_payment-plan"
            data-test-id="test-basicpolicydetails_payment-plan"
            aria-label="Payment Plan"
            sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <Typography
              id="basicpolicydetails_payment-plan-title"
              data-test-id="test-basicpolicydetails_payment-plan-title"
              aria-label="Payment Plan Title"
              sx={BasicInfoCardStyles.head}
            >
              {t("Payment Plan")}
            </Typography>
            <Link
              id="basicpolicydetails_payment-plan-link"
              data-test-id="test-basicpolicydetails_payment-plan-link"
              aria-label="Payment Plan Link"
              sx={{ ...BasicInfoCardStyles.content, cursor: "pointer" }}
              onClick={() => setBillingDetailsDialogOpen(true)}
            >
              {data?.paymentPlan}
            </Link>
          </Box>
        </Box>
      </Box>
      {/* <CustomDialog
        maxWidth="xl"
        onClose={() => setBillingDetailsDialogOpen(false)}
        open={billingDetailsDialogOpen}
        title={t("Payment Plans")}
        showDivider={true}
      ></CustomDialog> */}

      <CustomDrawer
        content={
          <CustomTable
            columns={columns}
            data={data?.policyHeaderBillingDetails || []}
          />
        }
        open={billingDetailsDialogOpen}
        title="Payment Plan"
        onClose={() => setBillingDetailsDialogOpen(false)}
      />
    </>
  );
};

export default BasicInfoCard;
